import { useContext } from "react";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import { Chip, IconButton, CircularProgress } from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  ModelBundleTag,
  useGetModelBundleTagsQuery,
  useCreateModelBundleTagMutation,
  useDeleteModelBundleTagMutation,
} from "../../../generated/graphql";
import { ModalInput } from "../../shared/Style/Style";
import { BundleFormModalContext } from "../modals/BundleFormModalContext";
import { TagOptionRow } from "../styles";

const filter = createFilterOptions<ModelBundleTag>();

export const SelectBundleTags = () => {
  const { modelData, updateModelData, errors, handleError } = useContext(
    BundleFormModalContext,
  );

  const { data, loading: tagsLoading } = useGetModelBundleTagsQuery({
    fetchPolicy: "cache-and-network",
  });
  const [createModelBundleTag, { loading: createTagLoading }] =
    useCreateModelBundleTagMutation();
  const [deleteModelBundleTag, { loading: deleteTagLoading }] =
    useDeleteModelBundleTagMutation();

  const availableTags: ModelBundleTag[] = data?.getModelBundleTags ?? [];
  const selectedTags: ModelBundleTag[] = modelData.tags ?? [];

  const handleChange = async (newValue: ModelBundleTag[]) => {
    const existing = newValue.filter((item) => item.id);
    const toCreate = newValue.filter((item) => !item.id);

    const created = await Promise.all(
      toCreate.map(async (item) => {
        const { data } = await createModelBundleTag({
          variables: { data: { name: item.name } },
          refetchQueries: ["getModelBundleTags"],
        });
        return data?.createModelBundleTag;
      })
    );

    updateModelData("tags", [...existing, ...created.filter(Boolean)]);
  };

  const handleDeleteTag = async (e: React.MouseEvent, tag: ModelBundleTag) => {
    e.stopPropagation();
    await deleteModelBundleTag({
      variables: { deleteModelBundleTagId: tag.id },
      refetchQueries: ["getModelBundleTags", "getModelBundlesPaginatedList"],
    });
    updateModelData(
      "tags",
      selectedTags.filter((t) => t.id !== tag.id),
    );
  };

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      loading={tagsLoading || createTagLoading || deleteTagLoading}
      loadingText={<CircularProgress color="inherit" size={20} />}
      options={availableTags}
      value={selectedTags}
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, value) => option.id === value.id}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const { inputValue } = params;
        if (!inputValue) return filtered;
        const exists = options.some((o) => o.name === inputValue);
        if (!exists) {
          filtered.push({ id: "", name: inputValue } as ModelBundleTag);
        }
        return filtered;
      }}
      renderOption={(option) =>
        option.id ? (
          <TagOptionRow>
            <span>{option.name}</span>
            <IconButton
              size="small"
              onClick={(e) => handleDeleteTag(e, option)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TagOptionRow>
        ) : (
          `Створити: "${option.name}"`
        )
      }
      onChange={(_, newValue) => handleChange(newValue as ModelBundleTag[])}
      onFocus={() => handleError("tags", false)}
      renderTags={(value: ModelBundleTag[], getTagProps) =>
        value.map((tag, index) => (
          <Chip
            key={tag.id}
            label={tag.name}
            deleteIcon={<CancelIcon fontSize="small" />}
            {...getTagProps({ index })}
            onDelete={() =>
              updateModelData(
                "tags",
                selectedTags.filter((t) => t.id !== tag.id),
              )
            }
          />
        ))
      }
      renderInput={(params) => (
        <ModalInput
          {...params}
          label="Внутрішні теги (для пошуку в списку)"
          error={errors?.tags}
          margin="none"
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
        />
      )}
    />
  );
};
